import { createContext, type ReactNode, useContext, useState } from 'react';
import { cn } from '../../lib/utils';
import { useIsPhoneLayout } from '../../lib/ui-layout';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within <Tabs>');
  }
  return context;
}

interface TabsProps {
  children: ReactNode;
  defaultTab: string;
  className?: string;
  onTabChange?: (id: string) => void;
}

export function Tabs({ children, defaultTab, className, onTabChange }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleSetActiveTab = (id: string) => {
    setActiveTab(id);
    onTabChange?.(id);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleSetActiveTab }}>
      <div className={className}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

interface TabListProps {
  children: ReactNode;
  className?: string;
}

export function TabList({ children, className }: TabListProps) {
  const phone = useIsPhoneLayout();

  // Phone: carousel chipy (phone-ui-standard §3) — ne dump desktop tabů v jedné řadě
  if (phone) {
    return (
      <div className={cn('phone-card-carousel', className)}>
        {children}
      </div>
    );
  }

  // Desktop: spodní linka, záložky se zalamují — posuvník v záhlaví je chyba.
  return (
    <div
      className={cn(
        'flex flex-wrap gap-1 border-b border-gray-800',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface TabProps {
  id: string;
  children: ReactNode;
  icon?: ReactNode;
}

export function Tab({ id, children, icon }: TabProps) {
  const { activeTab, setActiveTab } = useTabs();
  const phone = useIsPhoneLayout();
  const isActive = activeTab === id;

  if (phone) {
    return (
      <button
        type='button'
        onClick={() => setActiveTab(id)}
        className={cn('phone-card-chip', isActive && 'is-active')}
      >
        {icon ? <span className='shrink-0 opacity-80 [&_svg]:size-[13px]'>{icon}</span> : null}
        <span className='truncate'>{children}</span>
      </button>
    );
  }

  return (
    <button
      type='button'
      onClick={() => setActiveTab(id)}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap',
        isActive
          ? 'border-plex text-plex'
          : 'border-transparent text-gray-500 hover:text-gray-200',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

interface TabPanelProps {
  id: string;
  children: ReactNode;
  keepMounted?: boolean;
}

export function TabPanel({ id, children, keepMounted }: TabPanelProps) {
  const { activeTab } = useTabs();
  const phone = useIsPhoneLayout();
  const isActive = activeTab === id;
  const mt = phone ? 'mt-3' : 'mt-6';

  if (keepMounted) {
    // POZOR: ne `display: none` pro neaktivní tab — iframe (ttyd terminál) by
    // dostal layout 0×0, xterm.js by se připojil na tmux s 0 cols × 0 rows,
    // a po zviditelnění by tmux musel udělat full redraw = „přepisuje se".
    // Off-screen absolute zachová skutečné rozměry → xterm má validní grid.
    return (
      <div
        className={mt}
        style={isActive
          ? undefined
          : {
            position: 'absolute',
            left: '-99999px',
            top: 0,
            width: '100%',
            pointerEvents: 'none',
            visibility: 'hidden',
          }}
        aria-hidden={!isActive}
      >
        {children}
      </div>
    );
  }

  if (!isActive) return null;

  return <div className={mt}>{children}</div>;
}
